<template>
  <div
    class="cause-item f-w generic-border--thin-border"
    :class="causeClasses"
    @click="$emit('causeClick')"
  >
    <div class="cause-item__content v-centerer">
      <div class="cause-item__content__details">
        <div
          v-if="!editing"
          class="cause-item__content__details__text"
          :class="{ 'cause-item__content__details__text--empty': value.text === '' }"
          @click.stop="editing = true"
        >
          {{ value.text === '' ? $t('message.CAUSE') : safeString(value.text) }}
        </div>
        <EditableLabel
          v-else
          v-model="value.text"
          :placeholder="$t('message.CAUSE')"
          :disabled="disabled"
          use-textarea
          focus-on-mount
          @blur="editing = false"
          @input="$emit('textInput')"
          @startEdit="$emit('fieldClicked')"
        />
        <CauseVerificationCheckbox :cause="value" />
      </div>
      <div class="cause-item__content__img-wrapper" @click.stop="causePictureClick">
        <PictureCarousel :file-ids="value.files" no-arrows no-text />
      </div>
      <div
        class="cause-item__content__delete generic-border generic-border--icon-button generic-border--blue-border"
        @click.stop="deleteClicked"
      >
        <Icon>{{ deleteMedium }}</Icon>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Item in a causes list
 *
 * @vue-prop {Object} value - Need to be a Cause object
 * @vue-prop {Boolean} [selected=false] - Is the current cause selected or not
 * @vue-prop {Boolean} [disabled=false] - Disable this component will unallow any kind of editing
 * @vue-event {} pictureClick - Triggered when the image was clicked
 * @vue-event {Object} delete - Emit cause deleted, triggered when requesting deletion
 */

// Components
import CauseVerificationCheckbox from '@/components/cause-verification-checkbox.vue';
import EditableLabel from '@/components/editable-label.vue';
import Icon from '@/components/icon.vue';
import PictureCarousel from '@/components/picture-carousel.vue';
// Assets
import { safeString } from '@/utils/main';
import { deleteMedium, galleryMedium, selectMedium, unselectMedium } from '@/assets/icons';

export default {

  name: 'CauseItem',

  components: {
    CauseVerificationCheckbox,
    EditableLabel,
    Icon,
    PictureCarousel,
  },

  props: {
    value: {
      type: Object,
      required: true,
    },
    selected: Boolean,
    disabled: Boolean,
  },

  data() {
    return {
      deleteMedium,
      galleryMedium,
      selectMedium,
      unselectMedium,
      editing: false,
    };
  },

  computed: {
    causeClasses() {
      return {
        selected: this.selected,
      };
    },
  },

  methods: {
    safeString,
    causePictureClick() {
      if (!this.disabled) {
        this.$emit('pictureClick');
      }
    },
    deleteClicked() {
      if (!this.disabled) {
        window.calert(
          this.$t('message.CAUSE_DELETE_CONFIRM'),
          [
            { title: this.$t('message.CAUSE_DELETE'), action: this.confirmDelete, sClass: 'red' },
            { title: this.$t('message.CANCEL') },
          ],
        );
      }
    },
    confirmDelete() {
      this.$emit('delete', this.value);
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.cause-item {
  box-sizing: border-box;
  background: white;
  position: relative;
  cursor: pointer;

  &__content {
    display: flex;
    width: 100%;

    &__details {
      flex: 1;
      min-width: 0px;

      &__text {
        width: 100%;
        min-height: 2.3rem;
        font-weight: 600;
        margin-bottom: 0.3rem;
        padding: 0.5em;
        margin-top: -0.5rem;
        margin-left: -0.5rem;
        border-radius: $main-border-radius;
        box-sizing: border-box;
        white-space: pre-line;
        overflow-wrap: break-word;

        &:hover {
          background: $color-gray-background-hover;
        }

        &--empty {
          color: #999;
          font-style: italic;
        }
      }
    }

    &__img-wrapper {
      flex: 0 0 $main-component-height;
      margin-left: 0.5rem;
      min-width: 0px;
      width: $main-component-height;
      height: $main-component-height;

      img {
        display: block;
        height: auto;
        max-height: $main-component-height;
        max-width: $main-component-height;
        object-fit: contain; /* Not supported on old browser, not important if it doesn't work */
        width: 100%;
      }
    }

    &__delete {
      margin-left: 0.5rem;
      flex: 0 0 auto;
    }
  }

  &.selected {
    border: 1px solid $color-shizen-blue;
  }

  .editable-label {
    white-space: pre-line;
  }

  .editable-label > div.textarea {
    max-height: unset;
  }
}

@media (max-width: $smartphone-landscape) {
  .cause-item {
    padding: 3px;
    margin-top: 3px;

    td.img-wrapper {
      width: 60px;

      img {
        max-height: 60px;
        max-width: 60px;
      }
    }
  }
}
</style>
